import { defineStore } from 'pinia';
import currenciesJSON from "@/shared/assets/json/currencies.json";
import pricesJSON from "@/shared/assets/json/prices.json";
import { useLanguageStore } from "@/shared/stores/language.store";

const byCountry = new Map();
const byCode = new Map();
for (const key in currenciesJSON) {
	const cur = currenciesJSON[key];
	if (cur.code) byCode.set(cur.code, cur);
	if (Array.isArray(cur.countries)) {
		for (const country of cur.countries) byCountry.set(country, cur);
	}
}

const nfCache = new Map();

export const useCurrencyStore = defineStore('currency', {
	state: () => ({
		prices: pricesJSON,
		currencies: currenciesJSON,
		currCountry: null
	}),
	getters: {
		langStore: () => useLanguageStore(),
		currCurrency: state => byCountry.get(state.currCountry) || state.currencies['eur'],
		getCurrencyFromCode() {
			return (code) => {
				if (!code || typeof code.toUpperCase !== 'function') return this.currCurrency;
				return byCode.get(code.toUpperCase()) || this.currCurrency;
			}
		},
		currMinInteger() {
			let finalMinAmount = 1;
			if(this?.currCurrency?.hasOwnProperty('min_stripe_amount')) {
				finalMinAmount = Number(this.currCurrency.min_stripe_amount || 1);
			}
			return finalMinAmount;
		},
		readableCurrencyValue() {
			return (value, fromDb = true, withSymbol = true, noDecimals = false, customCurrencyCode = null) => {
				let currency = customCurrencyCode ? this.getCurrencyFromCode(customCurrencyCode) : this.currCurrency;
				let finalValue = !isNaN(value) ? value : 0;
				if(fromDb) {
					finalValue = this.dbToActualValue(finalValue, customCurrencyCode);
				}
				const lang = this.langStore.lang || "en";
				const key = currency.code + '|' + lang + '|' + (noDecimals ? 1 : 0);
				let nf = nfCache.get(key);
				if (!nf) {
					const opts = {
						maximumFractionDigits: noDecimals ? 0 : currency.decimals,
						minimumFractionDigits: noDecimals ? 0 : currency.decimals,
					};
					if (noDecimals) opts.roundingMode = "trunc";
					nf = new Intl.NumberFormat([lang], opts);
					nfCache.set(key, nf);
				}
				let formattedValue = nf.format(Number(finalValue || 0));
				let returnString = formattedValue;
				if(currency?.symbol && withSymbol) {
					returnString = currency?.symbol_position === 'before' ? currency.symbol +' '+ formattedValue : formattedValue +' '+ currency.symbol;
				}
				return returnString;
			}
		},
		dbToActualValue() {
			return (value, customCurrencyCode = null) => {
				let currency = customCurrencyCode ? this.getCurrencyFromCode(customCurrencyCode) : this.currCurrency;
				let multiplier = currency?.hasOwnProperty('multiplier') ? Number(currency.multiplier || 0) : 100;
				let decimals = currency?.hasOwnProperty('decimals') ? Number(currency.decimals || 0) : 2;
				let finalDbValue = !isNaN(value) ? Number(value) : 0;
				let finalValue = finalDbValue;
				if(!!multiplier && multiplier !== 1) {
					finalValue = Number(finalDbValue || 0) / multiplier;
				}
				return decimals === 0 ? Math.floor(finalValue) : Number(finalValue.toFixed(decimals));
			}
		},
		actualToDbValue: state => (value, customCurrencyCode = null) => {
			let currency = customCurrencyCode ? state.getCurrencyFromCode(customCurrencyCode) : state.currCurrency;
			let multiplier = currency?.hasOwnProperty('multiplier') ? Number(currency.multiplier || 0) : 100;
			let finalValue = !isNaN(value) ? Number(value || 0) : 0;
			if(!!multiplier && multiplier !== 1) {
				finalValue = finalValue * multiplier;
			}
			return Math.round(finalValue);
		},
		countryCurrency: state => country => byCountry.get(country) || state.currencies['eur'],
		priceFromId(state) {
			return (priceId, customCurrencyCode) => {
				if(!priceId) { return null; }
				let currency = customCurrencyCode ? this.getCurrencyFromCode(customCurrencyCode) : this.currCurrency;
				let foundPrice = state.prices?.[priceId]?.[TEST ? "test" : "live"]?.[0];
				if(!foundPrice) {
					foundPrice = currency?.prices?.[priceId]?.[TEST ? "test" : "live"] || null;
				}
				return foundPrice;
			}
		},
	},
	actions: {
		initCurrency(countryCode) {
			this.currCountry = countryCode;
		}
	},
});
